import { Dispatch, SetStateAction, SyntheticEvent, useEffect, useState } from "react";
import { Form, FormField, FormGroup, Label, Radio, Image, Icon, Dropdown } from "semantic-ui-react";

// Commons
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import TimeComboBox from "react-lib/apps/common/TimeComboBox";

// Components
import CreditInfoBox from "./CreditInfoBox";
import BroadcastPreview from "./BroadcastPreview";

// Interfaces
import { STYLES, ICONS, BTN_ACTS } from "./sequence/MeForm";

// Serializers
import PublishedDiagRuleNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";
import BroadcastPostSerializer from "@/issara-sdk/types/BroadcastPostSerializer_apps_PRX";

// Types
import {
  CreatedBroadcastData,
  FlatNode,
  PatientByLeafList,
  PickedState,
  PostCalculateCredit,
  TreeNode,
  UpdatedUserFirebase,
} from "./MeFormType";

// Utils
import moment from "moment";
import { dateToStringWithoutTimeBE, formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import { creditAmount } from "./MeFormUtils";
import { BroadcastMode } from "./MeCastType";

type PanelBroadcastInfoProps = {
  // config
  sequenceName: "MeForm" | "MeCast";
  isFormDetail: boolean;
  mode: BroadcastMode;
  // data
  content: string;
  selectedPdrNode: PublishedDiagRuleNodeSerializer | null;
  selectedPdrId?: number[];
  pdrNodeList: PublishedDiagRuleNodeSerializer[];
  patientByLeafList?: PatientByLeafList;
  disabledSend: boolean;
  broadcastData: CreatedBroadcastData;
  postCalculateCredit?: PostCalculateCredit;
  setBroadcastData: Dispatch<SetStateAction<CreatedBroadcastData>>;
  onChangeData: (e: SyntheticEvent | null, data: any) => void;
  // onPostBroadcast: (data: CreatedBroadcastData) => void;
  onSaveDraft: () => void;
  onSend: () => void;
  onOpenBuyCredit: () => void;
  onGetPatientByLeaf?: (pdrNodeId: number, nodeId: number, publishedDiagRuleId: number) => void;
  selectedPostBroadcast?: BroadcastPostSerializer | null;
  updatedByUserFirebase?: UpdatedUserFirebase;
} & Pick<PickedState, "BillingCreditWallet" | "buttonLoadCheck">;

type RenderGroupNameDropdownProps = {
  // config
  mode: BroadcastMode;
  // data
  receiverType: BroadcastPostSerializer["receiver_type"];
  selectedPdrNode: PublishedDiagRuleNodeSerializer | null;
  pdrNodeList: PublishedDiagRuleNodeSerializer[];
  patientByLeafList?: PatientByLeafList;
  onGetPatientByLeaf?: (pdrNodeId: number, nodeId: number, publishedDiagRuleId: number) => void;
  onChangeData: (e: SyntheticEvent | null, data: any) => void;
  selectedPdrId?: number[];
};

type DropdownItem = { id: number; nodeId: number | null };

const RenderGroupNameDropdown = (props: RenderGroupNameDropdownProps) => {
  const [dropdownList, setDropdownList] = useState<DropdownItem[]>([]);

  useEffect(() => {
    if (props.receiverType !== "SPECIFY") {
      setDropdownList([]);
      return;
    }

    if (props.selectedPdrId && props.selectedPdrId.length > 0) {
      setDropdownList(
        props.selectedPdrId.map((id) => ({
          id: id,
          nodeId: id,
        }))
      );
      return;
    }

    if (props.selectedPdrNode?.id) {
      setDropdownList([{ id: props.selectedPdrNode.id, nodeId: props.selectedPdrNode.id }]);
    } else {
      setDropdownList([{ id: 0, nodeId: null }]);
    }
  }, [props.receiverType, props.selectedPdrId, props.selectedPdrNode]);

  const handleAdd = () => {
    setDropdownList(prev => [
      ...prev,
      { id: Date.now(), nodeId: null },
    ]);
  };

  const handleRemove = (id: number) => {
    setDropdownList(prev => {
      const next = prev.filter(i => i.id !== id);
      const selectedIds = next.map(i => i.nodeId).filter(Boolean) as number[];

      props.onChangeData(null, {
        name: "published_diag_rule_nodes",
        value: selectedIds,
      });

      return next;
    });
  };

  const handleChange = (id: number, nodeId: number) => {
    const node = props.pdrNodeList.find(p => p.id === nodeId);

    if (node) {
      props.onGetPatientByLeaf?.(node.id as number, node.node_id as number, node.published_diag_rule);
    }

    setDropdownList(prev => {
      const next = prev.map(item =>
        item.id === id ? { ...item, nodeId } : item
      );

      const selectedIds = next.map(i => i.nodeId).filter(Boolean) as number[];
      props.onChangeData(null, {
        name: "published_diag_rule_nodes",
        value: selectedIds,
      });

      return next;
    });
  };

  if (props.receiverType === "ALL_MEMBER") return null;

  const selectedIds = dropdownList.map((item) => item.nodeId).filter(Boolean) as number[];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
      {dropdownList.map((item, idx) => {
        const options = props.pdrNodeList
          .filter((p) => !selectedIds.includes(p.id as number) || p.id === item.nodeId)
          .map((p) => ({ key: p.id, value: p.id, text: p.node_name }));

        return (
          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: "76%" }}>
              <Dropdown
                selection
                options={options}
                value={item.nodeId ?? ""}
                onChange={(e, { value }) => handleChange(item.id, value as number)}
                fluid
                // config
                disabled={props.mode === "VIEW"}
              />
            </div>
            <div style={{ width: "10%", textAlign: "center", color: "var(--primary-theme-color)" }}>
              {item.nodeId ? (
                <label>({props.patientByLeafList?.[item.nodeId]?.matched_patients ?? 0})</label>
              ) : (
                <label>(0)</label>
              )}
            </div>
            <div style={{ width: "7%" }}>
              {idx === dropdownList.length - 1 && (
                <Icon
                  name="add circle"
                  size="large"
                  onClick={handleAdd}
                  style={{ cursor: "pointer", color: "var(--primary-theme-color)" }}
                  // config
                  disabled={props.mode === "VIEW"}
                />
              )}
            </div>
            <div style={{ width: "7%" }}>
              {idx > 0 && (
                <Icon
                  name="minus circle"
                  size="large"
                  color="red"
                  onClick={() => handleRemove(item.id)}
                  style={{ cursor: "pointer" }}
                  // config
                  disabled={props.mode === "VIEW"}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PanelBroadcastInfo = (props: PanelBroadcastInfoProps) => {
  const [scheduleDate, setScheduleDate] = useState<string>("");
  const [scheduleTime, setScheduleTime] = useState<string>("");

  const { broadcastData, setBroadcastData } = props;
  const credit = props.postCalculateCredit?.credit_estimate_amount || "0.00";
  const isAmount = creditAmount(props.BillingCreditWallet.balance, credit) > 0;

  useEffect(() => {
    if (props.broadcastData.broadcast_type === "SCHEDULE") {
      if (props.selectedPostBroadcast?.schedule_broadcasted_at) {
        const scheduledAt = moment(props.selectedPostBroadcast.schedule_broadcasted_at);
        // Convert year to Buddhist Era
        const buddhistYear = scheduledAt.year() + 543;
        const formattedDate = scheduledAt.format(`DD/MM/`) + buddhistYear;
        setScheduleDate(formattedDate);
        setScheduleTime(scheduledAt.format("HH:mm"));
      } else {
        const now = moment();
        setScheduleDate(dateToStringWithoutTimeBE(now));
        setScheduleTime(now.format("HH:mm"));
      }
    }
  }, [props.broadcastData.broadcast_type, props.selectedPostBroadcast]);

  // const handleSaveDraft = () => {
  //   setBroadcastData((prev) => ({ ...prev, status: "DRAFTED" }));

  //   props.onPostBroadcast({
  //     ...broadcastData,
  //     status: "DRAFTED",
  //   });
  // };

  // const handleSend = () => {
  //   setBroadcastData((prev) => ({ ...prev, status: "PENDING_REVIEW" }));

  //   props.onPostBroadcast({
  //     ...broadcastData,
  //     status: "PENDING_REVIEW",
  //   });
  // };

  return (
    <div style={{ padding: "10px" }}>
      <Form>
        <FormGroup inline>
          <FormField>
            <label>Recipient(s)</label>
          </FormField>
          <FormField>
            <Radio
              label="All members"
              name="receiver_type"
              value="ALL_MEMBER"
              checked={broadcastData.receiver_type === "ALL_MEMBER"}
              onChange={props.onChangeData}
              // config
              disabled={props.mode === "VIEW"}
            />
          </FormField>
          <FormField>
            <Radio
              label="Specify"
              name="receiver_type"
              value="SPECIFY"
              checked={broadcastData.receiver_type === "SPECIFY"}
              onChange={props.onChangeData}
              // config
              disabled={props.mode === "VIEW"}
            />
          </FormField>
        </FormGroup>

        {broadcastData.receiver_type === "SPECIFY" && (
          <FormGroup>
            <FormField style={{ marginTop: 8 }}>
              <label>Group Name</label>
            </FormField>
            <FormField width={11}>
              <RenderGroupNameDropdown
                receiverType={broadcastData.receiver_type}
                selectedPdrNode={props.selectedPdrNode}
                pdrNodeList={props.pdrNodeList}
                patientByLeafList={props.patientByLeafList}
                onGetPatientByLeaf={props.onGetPatientByLeaf}
                onChangeData={props.onChangeData}
                selectedPdrId={broadcastData.published_diag_rule_nodes}
                // config
                mode={props.mode}
              />
            </FormField>
          </FormGroup>
        )}

        <FormGroup inline>
          <FormField>
            <Image style={{ width: "25px", height: "25px" }} src={ICONS.member} />
          </FormField>
          <FormField>
            <label>Total Member(s)</label>
          </FormField>
          <FormField>
            <Label
              style={STYLES.primaryThemeLabel}
              content={props.postCalculateCredit?.total_member_count || 0}
            />
          </FormField>
        </FormGroup>

        <FormGroup inline>
          <FormField width={3}>
            <label>Broadcasts Date</label>
          </FormField>
          <FormField style={{ display: "flex", alignItems: "center" }}>
            <Radio
              name="broadcast_type"
              value="INSTANTLY"
              checked={broadcastData.broadcast_type === "INSTANTLY"}
              onChange={props.onChangeData}
              // config
              disabled={props.mode === "VIEW"}
            />
          </FormField>
          <FormField style={{ display: "flex", alignItems: "center" }}>
            <label style={{ fontWeight: "normal" }}>Send Instantly</label>
          </FormField>
        </FormGroup>

        <FormGroup inline>
          <FormField width={3}>
            <label></label>
          </FormField>
          <FormField>
            <Radio
              name="broadcast_type"
              value="SCHEDULE"
              checked={broadcastData.broadcast_type === "SCHEDULE"}
              onChange={props.onChangeData}
              // config
              disabled={props.mode === "VIEW"}
            />
          </FormField>
          <FormField width={4} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ width: "100%" }}>
              <DateTextBox
                disabled={broadcastData.broadcast_type !== "SCHEDULE" || props.mode === "VIEW"}
                value={scheduleDate}
                inputFluid
                onChange={(dateStr: string) => {
                  setScheduleDate(dateStr);
                  props.onChangeData(null, {
                    name: "schedule_broadcasted_at",
                    date: dateStr,
                    time: scheduleTime,
                  });
                }}
              />
            </div>
          </FormField>
          <FormField width={3} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ width: "100%", marginTop: "4px" }}>
              <TimeComboBox
                disabled={broadcastData.broadcast_type !== "SCHEDULE" || props.mode === "VIEW"}
                style={{ width: "100%" }}
                defaultValue={scheduleTime}
                interval={30}
                maxHour={23}
                minHour={0}
                disableReorder
                fluid
                onTextChange={(timeStr: string) => {
                  setScheduleTime(timeStr);
                  props.onChangeData(null, {
                    name: "schedule_broadcasted_at",
                    date: scheduleDate,
                    time: timeStr,
                  });
                }}
              />
            </div>
          </FormField>
        </FormGroup>

        <FormGroup inline>
          <FormField width={11}>
            <CreditInfoBox
              // func & callback
              onOpenBuyCredit={props.onOpenBuyCredit}
              // config
              mode={props.mode}
              isAmount={isAmount}
              // data
              creditUsage={credit}
              // common
              BillingCreditWallet={props.BillingCreditWallet}
            />
          </FormField>
          <FormField width={3} style={{ paddingTop: "39px" }}>
            <ButtonLoadCheck
              paramKey={`${props.sequenceName}_${props.mode === "CREATE" ? "POST" : "UPDATE"}_BROADCAST_DRAFTED`}
              buttonLoadCheck={
                props.buttonLoadCheck?.[`${props.sequenceName}_${props.mode === "CREATE" ? "POST" : "UPDATE"}_BROADCAST_DRAFTED`]
              }
              onClick={props.onSaveDraft}
              size="mini"
              fluid
              title={
                <div
                  style={{
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <label style={{ cursor: "pointer" }}>Save Draft</label>
                  <Image style={{ width: "12px", height: "12px" }} src={ICONS.saveDraft} />
                </div>
              }
              // config
              disabled={props.mode === "VIEW"}
            />
          </FormField>
          <FormField width={2} style={{ paddingTop: "39px" }}>
            <ButtonLoadCheck
              paramKey={`${props.sequenceName}_${props.mode === "CREATE" ? "POST" : "UPDATE"}_BROADCAST_PENDING_REVIEW`}
              buttonLoadCheck={
                props.buttonLoadCheck?.[`${props.sequenceName}_${props.mode === "CREATE" ? "POST" : "UPDATE"}_BROADCAST_PENDING_REVIEW`]
              }
              onClick={props.onSend}
              size="mini"
              fluid
              disabled={props.disabledSend || props.mode === "VIEW" ||  !isAmount}
              style={STYLES.primaryThemeBtn}
              title={
                <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                  <label style={{ cursor: "pointer" }}>Send</label>
                  <Icon name="paper plane outline" />
                </div>
              }
            />
          </FormField>
        </FormGroup>

        {props.selectedPostBroadcast && (
          <FormGroup inline>
            <FormField>
              <label style={{ fontWeight: "normal", fontStyle: "italic" }}>Created by: {props.updatedByUserFirebase?.name} {formatISOtoBEDateTime(props.selectedPostBroadcast?.created_at).dateTimeShortBracket}</label>
            </FormField>
          </FormGroup>
        )}

        <FormGroup>
          <FormField width={8} style={{ paddingTop: "20px" }}>
            <BroadcastPreview content={props.content} />
          </FormField>
        </FormGroup>
      </Form>
    </div>
  );
};

export default PanelBroadcastInfo;
